import React from "react";

import { type LayoutKey, isoBoxFor } from "../../lib/antennaGeometry";

/**
 * The box drawn as a box: an isometric container with the antennas where they
 * physically sit.
 *
 * A flat grid is a truthful table but it is not a picture of anything; you
 * cannot look at it and say "the dark ones are all low on the door side". This
 * you can. The door label is not decoration — it is what makes left and right
 * mean something.
 *
 * Inline SVG on purpose. A 3D library for eight rows of dots would be a
 * dependency, a bundle and a rendering surface we do not need.
 *
 * TWO KINDS OF LAYOUT, and the difference is worth knowing when you read one:
 *
 *  - `reference-32` is READ FROM A DRAWING. Three shelf bays per wall, four
 *    antennas high, eight flat plates, antenna 1 on the ceiling at the door.
 *  - `txam-sb093` is MEASURED. Its antennas are placed from surveyed feet, and
 *    the tooltip carries the panel label and reference-sticker EPC that were
 *    photographed off each one. Four of its reader ports have no antenna at
 *    all; those are drawn hollow, outside the box.
 *
 * TWO KINDS OF DATA, likewise. With `reads` (the box read directly) each
 * antenna carries its own signal strength, so this is a real heat map. Without
 * it (the cloud fallback) only one antenna per tag is known, and a single lamp
 * lights — the cloud stores just the best-RSSI winner. The component adapts to
 * what it is given rather than being told which mode to be in.
 */

/** One antenna's view of the tag. */
export interface AntennaReadDetail {
  reads: number;
  best_rssi: number | null;
}

/**
 * Signal strength → colour, as a SEQUENTIAL single-hue ramp (light = weak,
 * dark = strong). Not a rainbow: magnitude gets one hue, and the eye reads
 * "darker = more" without a key.
 *
 * Steps are the project's `error` ramp at 500/600/700/900. Every one clears
 * 3:1 against the white chart surface, which matters because the port number
 * is drawn INSIDE the dot — a paler ramp looked better and made the number
 * unreadable. Radius carries the same signal a second time, so strength
 * survives greyscale printing and colour-vision differences.
 *
 * Thresholds are absolute dBm rather than ranked within the tag, so the same
 * colour means the same strength on every tag and box. RSSI is negative;
 * closer to zero is stronger.
 */
interface HeatStep {
  /** Lowest RSSI (dBm) that still counts as this step. */
  max: number;
  fill: string;
  r: number;
  label: string;
}

const HEAT_STEPS: HeatStep[] = [
  { max: -Infinity, fill: "#ef4444", r: 6.5, label: "weakest" },
  { max: -70, fill: "#dc2626", r: 7.0, label: "weak" },
  { max: -58, fill: "#b91c1c", r: 7.6, label: "strong" },
  { max: -45, fill: "#7f1d1d", r: 8.2, label: "strongest" },
];

function heatFor(rssi: number | null | undefined): { fill: string; r: number } {
  // No RSSI (cloud path) → the established single-lamp red, unchanged.
  if (rssi === null || rssi === undefined) return { fill: "#ef4444", r: 7 };
  let chosen = HEAT_STEPS[0];
  for (const step of HEAT_STEPS) if (rssi >= step.max) chosen = step;
  return { fill: chosen.fill, r: chosen.r };
}

export const AntennaBox3D: React.FC<{
  lit: Set<number>;
  layoutKey?: LayoutKey;
  /** Per-antenna detail when the box was read directly. Absent → cloud path. */
  reads?: Map<number, AntennaReadDetail>;
  /** Ports the box reports as switched off. Edge path only. */
  disabled?: Set<number>;
}> = ({ lit, layoutKey = "reference-32", reads, disabled }) => {
  const box = isoBoxFor(layoutKey);
  const hasHeat = !!reads && reads.size > 0;
  // The BOX's own is_active flag wins over the layout's hardcoded list. The
  // layout can only say which ports were never wired when it was surveyed;
  // the box knows what is switched off right now, which is a superset --
  // SB093's survey lists 4 unwired ports while the box disables 5, the extra
  // being a connector knocked loose on a site visit. Fall back to the layout
  // only when there is no live answer (the cloud path).
  const isOff = (port: number, layoutUnwired: boolean): boolean =>
    disabled ? disabled.has(port) : layoutUnwired;

  return (
    <svg
      width={box.width}
      height={box.height}
      viewBox={`0 0 ${box.width} ${box.height}`}
      role="img"
      aria-label="Antenna positions inside the box"
      className="max-w-full"
    >
      {box.edges.map((e, i) => (
        <line
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          x1={e.x1}
          y1={e.y1}
          x2={e.x2}
          y2={e.y2}
          stroke="currentColor"
          strokeWidth={1}
          className="text-secondary-300"
          strokeDasharray={e.faint ? "3 3" : undefined}
        />
      ))}

      <text
        x={box.doorLabel.x}
        y={box.doorLabel.y}
        textAnchor="middle"
        className="fill-secondary-400"
        style={{ fontSize: 9, letterSpacing: 0.5 }}
      >
        DOOR
      </text>

      {box.antennas.map((a) => {
        const detail = reads?.get(a.port);
        const off = isOff(a.port, !!a.unwired);
        const on = !off && (lit.has(a.port) || !!detail);
        const heat = on ? heatFor(detail?.best_rssi) : null;

        const tip = [
          off
            ? `Antenna ${a.port}${a.label ? ` · panel ${a.label}` : ""} · DISABLED`
            : `Antenna ${a.port}${a.label ? ` · panel ${a.label}` : ""}`,
          a.position ?? (a.surface === "ceiling" ? "ceiling" : `${a.surface} wall`),
          a.sticker ? `sticker ${a.sticker}` : null,
          off
            ? "switched off on the box — not listening, so reading nothing is expected"
            : detail
              ? `${detail.reads} read${detail.reads === 1 ? "" : "s"}`
                + (detail.best_rssi !== null ? ` · ${detail.best_rssi} dBm` : "")
              : "listening · did not read this tag",
          a.note,
        ]
          .filter(Boolean)
          .join(" · ");

        return (
          <g key={a.port}>
            <circle
              cx={a.x}
              cy={a.y}
              r={heat ? heat.r : 5.5}
              fill={heat ? heat.fill : undefined}
              stroke={heat ? heat.fill : undefined}
              className={
                // eslint-disable-next-line no-nested-ternary
                off
                  // Filled grey, not hollow: a disabled antenna is a definite
                  // state, and an empty ring reads as "no data" instead.
                  ? "fill-secondary-200 stroke-secondary-400"
                  : heat
                    ? ""
                    : "fill-white stroke-secondary-300"
              }
              strokeWidth={1}
              strokeDasharray={off ? "2 2" : undefined}
            >
              <title>{tip}</title>
            </circle>
            <text
              x={a.x}
              y={a.y + 2.5}
              textAnchor="middle"
              className={
                // eslint-disable-next-line no-nested-ternary
                off ? "fill-secondary-500" : on ? "fill-white" : "fill-secondary-400"
              }
              style={{ fontSize: 7, pointerEvents: "none" }}
            >
              {a.port}
            </text>
          </g>
        );
      })}

      {/* Scale key. Only when there is a gradient to explain — on the cloud
          path every lamp is the same red and a key would imply a distinction
          the data cannot make. */}
      {hasHeat && (
        <g transform={`translate(8, ${box.height - 12})`}>
          <text x={0} y={0} className="fill-secondary-400" style={{ fontSize: 8 }}>
            weak
          </text>
          {HEAT_STEPS.map((s, i) => (
            <rect
              key={s.fill}
              x={30 + i * 13}
              y={-7}
              width={12}
              height={8}
              rx={2}
              fill={s.fill}
            />
          ))}
          <text x={86} y={0} className="fill-secondary-400" style={{ fontSize: 8 }}>
            strong signal
          </text>
          {!!disabled?.size && (
            <g transform="translate(150, 0)">
              <circle cx={4} cy={-3} r={4.5} className="fill-secondary-200 stroke-secondary-400" strokeWidth={1} strokeDasharray="2 2" />
              <text x={13} y={0} className="fill-secondary-400" style={{ fontSize: 8 }}>
                disabled
              </text>
            </g>
          )}
        </g>
      )}
    </svg>
  );
};

export default AntennaBox3D;
